import { Factory, Stack } from "@phosphor-icons/react"
import { createFileRoute, Outlet, useNavigate } from "@tanstack/react-router"

import FinalizeBuyButton from "~/components/noos-supply-plan/buy-detail/FinalizeBuyButton"
import { Module } from "~/components/design-system/pages"
import { BreadcrumbWrapper } from "~/components/reusable/Breadcrumb"
import { type HeaderTab } from "~/components/reusable/HeaderTabs"
import LoadingOverlay from "~/components/reusable/LoadingOverlay"
import NotFound from "~/components/reusable/NotFound"
import { StatusBadge } from "~/components/reusable/StatusBadge"
import { StatusColor } from "~/constants/status"
import { BuyDetailProvider, useBuyDetailContext } from "~/context/BuyDetailProvider"
import { NoosSupplyPlanDetailProvider } from "~/context/NoosSupplyPlanDetailProvider"
import { useDeepLinkFilter } from "~/hooks/useDeepLinkFilter"
import { deriveBuyStatus, useGetBuy, type IBuyDetail } from "~/hooks/useSupplyPlan"
import { FilterScopeProvider } from "~/providers/FilterScopeProvider"
import { api } from "~/utils/api"

// `variantId`: set by Triggers' "Edit in buy", opens the buy filtered to that variant.
type BuySearch = { variantId?: string }

export const Route = createFileRoute(
  "/_auth/procurement/supply-plan/noos/$supplyPlanId/buys/$buyId/_layout",
)({
  // The router reads a numeric-looking id typed into the URL as a number.
  validateSearch: (search: Record<string, unknown>): BuySearch => ({
    variantId:
      typeof search.variantId === "string" || typeof search.variantId === "number"
        ? String(search.variantId) || undefined
        : undefined,
  }),
  component: RouteComponent,
})

const STATUS_COLOR = {
  Draft: StatusColor.Warning,
  Finalized: StatusColor.Success,
  Archived: StatusColor.Neutral,
} as const

function RouteComponent() {
  const { supplyPlanId, buyId } = Route.useParams()
  const supplyPlanIdNum = Number(supplyPlanId)
  const buyIdNum = Number(buyId)

  const { data: supplyPlans, isLoading: isLoadingSupplyPlans } = api.supplyPlan.getAll.useQuery()
  const { data: buy, isLoading: isLoadingBuy } = useGetBuy(buyIdNum)

  if (isLoadingSupplyPlans || isLoadingBuy) return <LoadingOverlay />

  const supplyPlan = supplyPlans?.find((p) => p.id === supplyPlanIdNum)
  if (!supplyPlan || !buy) return <NotFound />

  return (
    <NoosSupplyPlanDetailProvider supplyPlan={supplyPlan}>
      {/* Shared filter scope so a filter applied on the Quantities tab persists to the
          Capacity tab (and vice-versa). Keyed by the layout route so both sibling tabs
          resolve the same scope instead of one per leaf route. */}
      <FilterScopeProvider scopeKey={Route.id}>
        <VariantLinkFilter />
        <BuyDetailProvider
          buy={buy}
          scopePath={`/procurement/supply-plan/noos/${supplyPlanId}/buys/${buyId}`}
        >
          <BuyDetailModule supplyPlanName={supplyPlan.name} buy={buy} buyId={buyIdNum} />
        </BuyDetailProvider>
      </FilterScopeProvider>
    </NoosSupplyPlanDetailProvider>
  )
}

// Inside the filter scope: applies the linked variant for this visit, then drops it from the URL.
function VariantLinkFilter() {
  const { variantId } = Route.useSearch()
  const navigate = useNavigate()
  useDeepLinkFilter(
    variantId
      ? { field: "variantId", operator: "oneOf", value: "", values: [variantId], by: "variantId" }
      : undefined,
    () =>
      void navigate({
        to: ".",
        search: (prev: Record<string, unknown>) => ({ ...prev, variantId: undefined }),
        replace: true,
      }),
  )
  return null
}

// Separate component because the capacity-violation tab badge needs useBuyDetailContext,
// which is only available inside BuyDetailProvider.
function BuyDetailModule({
  supplyPlanName,
  buy,
  buyId,
}: {
  supplyPlanName: string
  buy: IBuyDetail
  buyId: number
}) {
  const { capacity } = useBuyDetailContext()
  const violationCount = capacity.violations.length
  const buyStatus = deriveBuyStatus(buy.purchasePlan)

  const tabs: HeaderTab[] = [
    {
      label: "Quantities",
      icon: <Stack />,
      path: "/procurement/supply-plan/noos/$supplyPlanId/buys/$buyId/quantities" as const,
    },
    {
      label: "Vendor Capacity",
      icon: <Factory />,
      path: "/procurement/supply-plan/noos/$supplyPlanId/buys/$buyId/capacity" as const,
      count: violationCount > 0 ? violationCount : undefined,
      countVariant: "red",
      tooltip:
        violationCount > 0
          ? `${violationCount} capacity ${violationCount === 1 ? "violation" : "violations"} as a result of this buy`
          : undefined,
    },
  ]

  return (
    <Module
      breadcrumb={
        <>
          <BreadcrumbWrapper
            items={[
              { label: "Procurement", href: "/procurement" },
              { label: "Supply Plan", href: "/procurement/supply-plan" },
              {
                label: supplyPlanName,
                href: "/procurement/supply-plan/noos/$supplyPlanId/overview",
              },
              { label: buy.purchasePlan.name },
            ]}
          />
          <StatusBadge color={STATUS_COLOR[buyStatus]} text={buyStatus} />
        </>
      }
      actions={buyStatus === "Draft" ? <FinalizeBuyButton buyId={buyId} /> : undefined}
      tabs={{ items: tabs, path: "/procurement/supply-plan/noos/$supplyPlanId/buys/$buyId" }}
    >
      <Outlet />
    </Module>
  )
}
